import { useState } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { XCircle } from "lucide-react";

export default function CloseTreatmentModal({
  patient,
  activeTreatment,
  visits,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ VALIDATION
  const canCloseTreatment = () => {
    if (!visits.length) return false;

    // ❌ unpaid consultation
    const hasUnpaidConsultation = visits.some(
      (v) =>
        v.visit_category !== "TREATMENT" &&
        v.consultation_payment_status === "UNPAID"
    );

    // ❌ unpaid treatment
    const hasUnpaidTreatment = visits.some(
      (v) =>
        v.visit_category === "TREATMENT" &&
        v.treatment_payment_status !== "PAID"
    );

    return !hasUnpaidConsultation && !hasUnpaidTreatment;
  };

  const handleCloseTreatment = async () => {
  if (!canCloseTreatment()) {
    setError("Please clear all pending payments before closing treatment");
    return;
  }

  const latestTreatment = activeTreatment?.[0];

  if (!latestTreatment) {
    setError("No active treatment found");
    return;
  }

  setLoading(true);
  setError("");

  try {
    await updateDoc(doc(db, "treatments", latestTreatment.id), {
      status: "COMPLETED",
      completed_at: serverTimestamp(),
    });

    await updateDoc(doc(db, "patients", patient.id), {
      treatment_active: false,
      current_treatment_id: null,
      current_treatment_name: null,
      updated_at: serverTimestamp(),
    });

    onSuccess();
    onClose();
  } catch (err) {
    console.error(err);
    setError("Something went wrong while closing treatment");
  }

  setLoading(false);
};

  return (
   <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
  <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">

    {/* HEADER */}
    <div className="flex items-center justify-between px-5 py-4 border-b">
      <h2 className="text-lg font-semibold text-[#204080]">
        Close Treatment
      </h2>

      <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
        <XCircle size={20} />
      </button>
    </div>

    {/* BODY */}
    <div className="p-5">
      {/* ICON + MESSAGE */}
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-red-100 text-red-600 p-2 rounded-full">
          <XCircle size={20} />
        </div>
        <p className="text-sm text-gray-600">
          You can close treatment only if all consultation and treatment
          payments are completed.
        </p>
      </div>

      {/* ERROR */}
      {error && (
        <p className="text-red-500 text-sm mb-3">{error}</p>
      )}

      {/* STATUS */}
    {/* STATUS */}
<div className="text-sm space-y-2 mb-4">
  {visits.some(
    (v) =>
      v.visit_category !== "TREATMENT" &&
      v.consultation_payment_status === "UNPAID"
  ) && (
    <p className="text-red-600">
      • Consultation fee pending — please check
    </p>
  )}

  {visits.some(
    (v) =>
      v.visit_category === "TREATMENT" &&
      v.treatment_payment_status !== "PAID"
  ) && (
    <p className="text-red-600">
      • Treatment payment pending — please check
    </p>
  )}

  {/* ✅ Optional: show success only if nothing pending */}
  {!visits.some(
    (v) =>
      (v.visit_category !== "TREATMENT" &&
        v.consultation_payment_status === "UNPAID") ||
      (v.visit_category === "TREATMENT" &&
        v.treatment_payment_status !== "PAID")
  ) && (
    <p className="text-green-600 font-medium">
      All payments cleared. You can close treatment.
    </p>
  )}
</div>
    </div>

    {/* FOOTER */}
    <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm rounded-lg bg-gray-200 hover:bg-gray-300"
      >
        Cancel
      </button>

      <button
        onClick={handleCloseTreatment}
        disabled={!canCloseTreatment() || loading}
        className={`px-4 py-2 text-sm rounded-lg text-white ${
          !canCloseTreatment() || loading
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-red-500 hover:bg-red-600"
        }`}
      >
        {loading ? "Closing..." : "Close Treatment"}
      </button>
    </div>
  </div>
</div>
  );
}